import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import SortIcon from '@mui/icons-material/Sort';
import OvPreviewAsSelect from './OvPreviewAsSelect';
import { VideoPlacement } from '../../../common/model/dto/video/video-placement';
import { PreviewAudience } from '../../../common/model/type/video-placement.types';
import breakpoints from '../../../design-system/breakpoints';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';

interface OvVideoPlacementPreviewProps {
  placementKey: string;
  placement: VideoPlacement | undefined;
  arePlacementsLoaded: boolean;
  audience: PreviewAudience;
  unknownGoals: string[];
  onAudienceChange: (audience: PreviewAudience) => void;
}

const OvVideoPlacementPreview: FC<OvVideoPlacementPreviewProps> = ({
  placementKey,
  placement,
  arePlacementsLoaded,
  audience,
  unknownGoals,
  onAudienceChange,
}) => {
  const { t } = useTranslation();

  return (
    <Bar aria-label={t('videos.preview.region')}>
      <Placement>
        {placement && <Description>{placement.description}</Description>}
        <Key>{placementKey}</Key>
        {!placement && arePlacementsLoaded && (
          <Note>{t('videos.placement.retired')}</Note>
        )}
      </Placement>
      <SortedBy>
        <SortIcon aria-hidden="true" />
        {t('videos.preview.sortedByOrder')}
      </SortedBy>
      <Audience>
        <OvPreviewAsSelect
          audience={audience}
          unknownGoals={unknownGoals}
          onChange={onAudienceChange}
        />
        <Helper>
          {audience.kind === 'everyone' && (
            <span>{t('videos.preview.everyoneHint')}</span>
          )}
          <span>{t('videos.preview.dimmedHint')}</span>
        </Helper>
      </Audience>
    </Bar>
  );
};

export default OvVideoPlacementPreview;

const Bar = styled.section`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${Variables.spacing.SMALL} ${Variables.spacing.XLARGE};
  margin-top: ${Variables.spacing.MEDIUM};
  padding: ${Variables.spacing.SMALL} ${Variables.spacing.MEDIUM};
  border: 1px solid ${Colours.OV_BORDER_COLOR_V3};
  border-radius: ${Variables.borderRadius.MEDIUM};
  background-color: ${Colours.OV_LIGHT_BLUE};
  color: ${Colours.OV_BASE};
`;

const Placement = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const Description = styled.span`
  font-weight: bold;
`;

const Key = styled.span`
  font-family: monospace;
  font-size: ${Variables.fontSizes.SMALL};
  color: ${Colours.OV_CHARCOAL};
  overflow-wrap: anywhere;
`;

const Note = styled.span`
  font-size: ${Variables.fontSizes.SMALL};
  color: ${Colours.OV_CHARCOAL};
`;

const SortedBy = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: ${Variables.fontSizes.SMALL};

  .MuiSvgIcon-root {
    width: 1rem;
    height: 1rem;
  }
`;

const Audience = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${Variables.spacing.SMALL};

  @media (max-width: ${breakpoints.lg}) {
    flex-basis: 100%;
  }
`;

const Helper = styled.p`
  display: flex;
  flex-direction: column;
  margin: 0;
  font-size: ${Variables.fontSizes.SMALL};
  color: ${Colours.OV_CHARCOAL};
`;
